<template>
  <div id="staff-list" class="zgb-list-wrap">
    <v-card class="elevation-6 rounded-lg">
      <v-sheet class="list-header" tile>
        <div class="header-top">
          <!-- Top Left: Title with Icon -->
          <div class="header-title-section">
            <div class="title-icon-wrapper">
              <v-icon large color="white" class="title-icon">mdi-account-tie</v-icon>
            </div>
            <div class="title-content">
              <h2 class="page-title">{{ PageTitle }}</h2>
              <p class="page-desc">{{ PageDescription }}</p>
            </div>
          </div>

          <!-- Top Right: Actions -->
          <div class="header-actions-top">
            <v-chip
              small
              color="rgba(255, 255, 255, 0.25)"
              text-color="white"
              class="year-chip-top"
            >
              <v-icon left x-small>mdi-calendar-check</v-icon>
              {{ YearName }}
            </v-chip>

            <v-btn
              icon
              small
              color="white"
              class="action-btn"
              @click.prevent="refreshPageData"
              v-tooltip="'Refresh List'"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>

            <v-btn
              color="white"
              small
              class="action-btn-primary"
              @click.prevent="addRecordPrompt = true"
            >
              <v-icon left small>mdi-plus-circle</v-icon> Assign Member
            </v-btn>
          </div>
        </div>

        <!-- Bottom: Search Bar -->
        <div class="header-search">
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            dense
            hide-details
            rounded
            clearable
            placeholder="Search by member name, designation, or LOM..."
            class="search-input-modern"
          ></v-text-field>
        </div>
      </v-sheet>

      <v-card-text>
        <div class="loading-wrap" v-if="LoadingFlag">
          <v-progress-circular
            indeterminate
            size="44"
            color="primary"
          ></v-progress-circular>
        </div>

        <v-container fluid>
          <v-row class="summary-row" align="center" justify="space-between">
            <v-col cols="12" md="6">
              <div class="found-text" v-if="rows && rows.length > 0">
                <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                <strong class="text-primary">{{ rows.length }}</strong> member(s)
                assigned.
              </div>
              <div v-else-if="ResultFlag && rows.length == 0" class="muted">
                <v-icon small color="warning" class="mr-2">mdi-alert-circle</v-icon>
                No members assigned yet.
              </div>
            </v-col>
            <v-col cols="12" md="6" class="text-right d-none d-md-flex">
              <v-chip small color="indigo lighten-5" text-color="indigo" class="mr-2">
                <v-icon left x-small color="indigo">mdi-account-multiple-check</v-icon>
                {{ rows.length || 0 }} Total
              </v-chip>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-data-iterator
                :items="rows"
                :items-per-page="-1"
                hide-default-footer
              >
                <template v-slot:default="props">
                  <v-row dense>
                    <v-col
                      v-for="item in props.items"
                      :key="item.MemberId"
                      cols="12"
                      sm="6"
                      md="4"
                      lg="3"
                    >
                      <v-card class="member-card" hover elevation="4">
                       <div class="card-header-badge">
                          <v-chip
                            x-small
                            :color="item.ActiveStatus == 1 ? 'green' : 'red'"
                            text-color="white"
                            class="position-chip"
                          >
                            <v-icon x-small left>
                              {{
                                item.ActiveStatus == 1
                                  ? "mdi-check"
                                  : "mdi-close"
                              }}
                            </v-icon>
                            {{ item.ActiveStatusTxt }}
                          </v-chip>
                        </div>

                        <div class="card-top d-flex">
                          <v-avatar size="88" class="avatar-elev">
                            <v-img
                              :src="item.ProfilePic"
                              :alt="item.MemberFullname"
                            />
                          </v-avatar>

                          <div class="card-meta pl-4">
                            <div class="member-name">
                              {{ item.MemberFullname }}
                            </div>
                            <div class="member-designation caption">
                              <v-icon x-small class="mr-1">mdi-badge-account</v-icon>
                              {{ item.DesignationName || "—" }}
                            </div>
                            <div class="member-lom caption">
                              <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
                              {{ item.LomName || "—" }}
                            </div>
                          </div>
                        </div>

                        <v-divider class="my-3"></v-divider>

                        <v-card-actions class="card-actions">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                color="primary"
                                v-bind="attrs"
                                v-on="on"
                                @click="editData(item)"
                                class="edit-btn"
                              >
                                <v-icon>mdi-pencil</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit Member</span>
                          </v-tooltip>

                          <v-spacer></v-spacer>
                        </v-card-actions>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </template>
              </v-data-iterator>
            </v-col>
          </v-row>

          <v-row class="notes-row mt-6">
            <v-col cols="12" md="8">
              <h4 class="notes-title">Important</h4>
              <ol class="notes-content">
                <li>All elective post members should be active members.</li>
                <li>
                  Elective officers must upload age proof under their profile
                  update page.
                </li>
                <li>Use "Assign Member" to add or update Year assignments.</li>
              </ol>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- Add Dialog -->
    <v-dialog v-model="addRecordPrompt" persistent scrollable max-width="70%">
      <div style="max-height: 80vh; overflow-y: auto">
        <zgb-team-members-modify
          pageDescription="Assign ZGB members"
          :addRecordPrompt="addRecordPrompt"
          :ZoneCode="ZoneCode"
          @hideAddRecordPrompt="hideAddRecordPrompt"
          v-if="addRecordPrompt"
        />
      </div>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog
      v-model="editRecordPrompt"
      persistent
      scrollable
      max-width="70%"
    >
      <div style="max-height: 80vh; overflow-y: auto">
        <zgb-team-members-edit
          pageDescription="Edit ZGB members"
          :editRecordPrompt="editRecordPrompt"
          :recordData="selectedData"
          @hideEditRecordPrompt="hideEditRecordPrompt"
          v-if="editRecordPrompt"
        />
      </div>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";
import ZgbTeamMembersModify from "@/view/pages/erp/team-members/zgb/ZgbTeamMembersModify.vue";
import ZgbTeamMembersEdit from "@/view/pages/erp/team-members/zgb/ZgbTeamMembersEdit.vue";

export default {
  mixins: [common],
  components: {
    ZgbTeamMembersModify,
    ZgbTeamMembersEdit,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,

      floatButtonFlag: false,

      rows: [],
      tableSelectFlag1: false,
      tableColumns1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      editFlag: true,
      selectedData: {},

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      zone: {},
      lom: {},

      name: "",
      nameRules: [
        (v) => !!v || "Name is required",
        (v) => (v && v.length <= 10) || "Name must be less than 10 characters",
      ],
      email: "",
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],
      select: "Item 1",
      items: ["Item 1", "Item 2", "Item 3", "Item 4"],
      checkbox: false,

      ZoneCodeRules: [(v) => !!v || "Zone is required"],
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    SearchId: function () {
      console.log("watch SearchId");
      this.rows = [];
    },
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        console.log({ LomId });
        this.LomCode = LomId;

        var ZoneId = this.$session.get("ZoneId");
        ZoneId = ZoneId == (null || undefined) ? "" : ZoneId;
        console.log({ ZoneId });
        this.ZoneCode = ZoneId;

        this.pageData();
      }
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "zgb",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getTableRecords();
    },
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      this.getTableRecords();
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      this.getTableRecords();
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one member to edit";
        this.toast("error", message);
      }
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var ZoneCode = this.ZoneCode;
      console.log({ ZoneCode });

      if (ZoneCode > 0) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-designation/lists";
        var upload = {
          UserInterface: 4,
          OrganizationTypeId: 2,
          YearCode: this.JciYearCode,
          ZoneCode: ZoneCode,
          ExceptDesignationId: [1, 5, 7],
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.rows = [];
        this.ResultFlag = false;
        this.LoadingFlag = true;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.ResultFlag = true;
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.rows = records.TableData;
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableSelectFlag1 = records.TableSelectFlag;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.toast("error", error);
            thisIns.ResultFlag = true;
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Your session is expired. Kindly login again. ";
        console.log("error=" + message);
      }
    },
    getZoneCodeOptions(source, destination) {
      console.log("getZoneCodeOptions called");
      // this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
      var CurrentYearId = this.CurrentYearId;
      console.log(
        "source=" +
          source +
          ", destination=" +
          destination +
          ", CurrentYearId=" +
          CurrentYearId
      );
      if (source != "" && destination != "") {
        this.LoadingFlag = true;
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: CurrentYearId,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getLomCodeOptions(source, destination, ZoneCode) {
      console.log("getLomCodeOptions called");
      console.log(
        "source=" +
          source +
          ", destination=" +
          destination +
          ", ZoneCode=" +
          ZoneCode
      );
      var CurrentYearId = this.CurrentYearId;
      if (ZoneCode != "" && CurrentYearId != "") {
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: CurrentYearId,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "Zone should not be empty";
        this.toast("error", message);
      }
    },
    getYearCodeOptions(source, destination) {
      console.log("getYearCodeOptions called");
      // this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
      // var CurrentYearId = this.CurrentYearId;
      var CurrentYearId = 1;
      console.log(
        "source=" +
          source +
          ", destination=" +
          destination +
          ", CurrentYearId=" +
          CurrentYearId
      );
      if (source != "" && destination != "") {
        this.LoadingFlag = true;
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/jci-year/options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    searchForm() {
      console.log("searchForm is called");

      var validate1 = this.$refs.form2.validate();
      var SearchId = this.SearchId;
      var UserInterface = SearchId == 1 ? 1 : 3;
      console.log(
        "validate1=" +
          validate1 +
          ", SearchId=" +
          SearchId +
          ", UserInterface=" +
          UserInterface
      );

      if (validate1) {
        this.SearchFlag = true;
        this.rows = [];
        this.zone = {};
        this.lom = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members";
        var upload = {
          UserInterface: UserInterface,
          additional: {
            MembershipId: this.MembershipId,
            LomCode: this.FromZoneCode,
            LomCode: this.FromLomCode,
          },
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SearchFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.toast("success", output);
              thisIns.rows = records;
              thisIns.zone = response.data.zone;
              thisIns.lom = response.data.lom;
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SearchFlag = false;
          });
      } else {
        var message = "Kindly fill the required fields";
        this.toast("error", message);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
/* filepath: /home/hplap/Documents/web_app/jci_india_erp_app_new/src/view/pages/erp/team-members/zgb/ZgbTeamMembersList.vue */
/* Visual refresh: professional, compact list cards. No logic changes. */

#staff-list {
  font-family: "Inter", "Roboto", sans-serif;

  .list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    background: linear-gradient(90deg, #f8fafc, #ffffff);
    border-radius: 8px;
    margin-bottom: 12px;
  }

  .header-left .page-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
  }
  .page-desc {
    color: #556070;
    font-size: 13px;
    margin-top: 6px;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .search-input {
    max-width: 420px;
    background: #fff;
    border-radius: 10px;
  }

  .found-text {
    color: #1f2937;
    font-weight: 600;
  }

  .member-card {
    border-radius: 12px;
    overflow: hidden;
    background: #ffffff;
    transition: transform 0.16s ease, box-shadow 0.16s ease;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.03);
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .member-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 18px 42px rgba(11, 22, 60, 0.08);
  }

  .card-top {
    display: flex;
    align-items: center;
    padding: 16px;
  }

  .avatar-elev {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
  }

  .card-meta {
    flex: 1;
  }
  .member-name {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }
  .member-designation {
    color: #2563eb;
    margin-top: 6px;
  }
  .member-lom {
    color: #ef4444;
    margin-top: 6px;
  }

  .card-body {
    padding: 12px 16px;
  }
  .info-row {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4b5563;
    margin-bottom: 6px;
  }

  .card-actions {
    padding: 10px 12px;
    display: flex;
    align-items: center;
  }

  .notes-row {
    margin-top: 18px;
  }

  .notes-title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
  }

  .notes-content li {
    margin-bottom: 8px;
    color: #4b5563;
    line-height: 1.6;
  }

  /* Header Alignment */
  .list-header {
    background: linear-gradient(90deg, #1976d2 0%, #4e8ef7 100%);
    padding: 20px 24px;
  }

  .header-content {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .header-left {
    flex: 1;
    min-width: 0;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  }

  .page-title {
    color: #ffffff;
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    white-space: nowrap;
  }

  .year-chip {
    white-space: nowrap;
    background: rgba(255, 255, 255, 0.25) !important;
    color: #ffffff !important;
  }

  .page-desc {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    font-weight: 500;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    white-space: nowrap;
  }

  .search-input {
    min-width: 240px !important;
  }

  .search-input ::v-deep .v-input__control,
  .search-input ::v-deep input {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 6px;
  }

  /* Responsive */
  @media (max-width: 960px) {
    .list-header {
      padding: 16px;
    }

    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .header-left {
      width: 100%;
    }

    .title-row {
      flex-wrap: wrap;
    }

    .page-title {
      font-size: 18px;
    }

    .header-actions {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .search-input {
      min-width: 100% !important;
    }

    .search-input ::v-deep .v-btn {
      width: 100%;
    }
  }

  @media (max-width: 600px) {
    .title-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .year-chip {
      align-self: flex-start;
    }

    .page-title {
      font-size: 16px;
    }
  }
}
</style>
// ...existing code...